import React, {
  HTMLAttributes,
  useLayoutEffect,
  useRef
} from 'react';

type MathJaxApi = {
  chtml?: {
    fontURL: string;
  };
  options?: {
    enableMenu: boolean;
  };
  startup?: {
    promise?: Promise<void>;
    typeset?: boolean;
  };
  typesetClear?: (elements: HTMLElement[]) => void;
  typesetPromise?: (elements: HTMLElement[]) => Promise<void>;
};

type MathJaxWindow = Window & {
  MathJax?: MathJaxApi;
};

const MATH_JAX_SCRIPT_ID = 'mathjax-script';
const MATH_JAX_ASSET_ROOT = `${process.env.PUBLIC_URL || ''}/assets/mathjax`;
const MATH_JAX_SCRIPT_SRC = `${MATH_JAX_ASSET_ROOT}/mml-chtml.js`;
const MATH_JAX_FONT_URL = `${MATH_JAX_ASSET_ROOT}/output/chtml/fonts/woff-v2`;

let typesettingQueue = Promise.resolve();
let mathJaxLoadPromise: Promise<MathJaxApi> | null = null;

const getMathJax = (): MathJaxApi | undefined => {
  return (window as MathJaxWindow).MathJax;
};

const isMathJaxReady = (mathJax: MathJaxApi): boolean => {
  return Boolean(
    mathJax?.startup?.promise
    && mathJax.typesetClear
    && mathJax.typesetPromise
  );
};

const loadMathJax = (): Promise<MathJaxApi> => {
  const existingMathJax = getMathJax();

  if (existingMathJax && isMathJaxReady(existingMathJax)) {
    return existingMathJax.startup.promise.then(() => existingMathJax);
  }

  if (mathJaxLoadPromise) {
    return mathJaxLoadPromise;
  }

  mathJaxLoadPromise = new Promise((resolve, reject) => {
    (window as MathJaxWindow).MathJax = {
      chtml: {
        fontURL: MATH_JAX_FONT_URL
      },
      options: {
        enableMenu: false
      },
      startup: {
        typeset: false
      }
    };

    const script = document.createElement('script');
    script.id = MATH_JAX_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = MATH_JAX_SCRIPT_SRC;

    script.addEventListener('load', () => {
      const loadedMathJax = getMathJax();

      if (!loadedMathJax || !isMathJaxReady(loadedMathJax)) {
        reject(new Error('MathJax loaded without the required MathML typesetting API.'));
        return;
      }

      loadedMathJax.startup.promise
        .then(() => resolve(loadedMathJax))
        .catch(reject);
    });
    script.addEventListener('error', () => {
      reject(new Error('Failed to load MathJax.'));
    });

    document.head.appendChild(script);
  }).catch(error => {
    mathJaxLoadPromise = null;
    throw error;
  });

  return mathJaxLoadPromise;
};

const queueTypeset = (element: HTMLDivElement, mathJax: MathJaxApi): void => {
  typesettingQueue = typesettingQueue
    .catch(() => undefined)
    .then(async () => {
      if (!element.isConnected) {
        return;
      }

      mathJax.typesetClear([element]);
      await mathJax.typesetPromise([element]);
    });
};

export const MathJaxContent = ({
  children,
  ...otherProps
}: HTMLAttributes<HTMLDivElement>): JSX.Element => {
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = contentRef.current;

    if (!element) {
      return undefined;
    }

    let isCurrentRender = true;

    loadMathJax()
      .then(mathJax => {
        if (isCurrentRender) {
          queueTypeset(element, mathJax);
        }
      })
      .catch(() => undefined);

    return () => {
      isCurrentRender = false;
    };
  }, [children]);

  return (
    <div {...otherProps} ref={contentRef}>
      {children}
    </div>
  );
};
